import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, Users, Calendar, Award } from 'lucide-react';
import { images, restaurantInfo } from '../data/mock';

const VIPSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const features = [
    {
      icon: <Users size={28} />,
      title: 'Privatisation',
      description: 'Espaces privés pour vos événements exclusifs'
    },
    {
      icon: <Calendar size={28} />,
      title: 'Sur-mesure',
      description: 'Service personnalisé adapté à vos besoins'
    },
    {
      icon: <Award size={28} />,
      title: 'Excellence',
      description: 'Équipe dédiée pour une expérience parfaite'
    }
  ];

  return (
    <section
      id="privatisation"
      ref={sectionRef}
      className="py-24 md:py-32 bg-white relative overflow-hidden"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #000 1px, transparent 1px)',
          backgroundSize: '30px 30px'
        }}></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="max-w-4xl mx-auto text-center mb-20">
          <div
            className={`transform transition-all duration-1000 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="inline-block bg-black text-white px-4 py-2 rounded-md text-sm font-semibold mb-6">
              💎 Privatisation
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-black mb-8 leading-tight">
              <span style={{ color: '#501414' }}>Expérience sur mesure</span>
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed">
              Privatisez, célébrez, vivez. Notre équipe vous accompagne pour créer des
              moments sur-mesure, dans un cadre exclusif et raffiné.
            </p>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          {/* Image Gallery - Two Photos */}
          <div
            className={`transform transition-all duration-1000 delay-200 ${
              isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
            }`}
          >
            {/* Main VIP Image */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl mb-6">
              <img
                src={images.vip[0]}
                alt="VIP Lounge"
                className="w-full h-[400px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
            </div>
            
            {/* Second Image */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={images.vip[1]}
                alt="Private Dining"
                className="w-full h-[300px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
            </div>
          </div>

          {/* Features */}
          <div
            className={`transform transition-all duration-1000 delay-400 ${
              isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
            }`}
          >
            <div className="space-y-6">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-gray-50 rounded-xl p-6 hover:bg-gray-100 transition-all duration-300 hover:transform hover:-translate-y-1 hover:shadow-lg border border-gray-200"
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center flex-shrink-0">
                      {React.cloneElement(feature.icon, { className: 'text-white' })}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-black mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 p-8 rounded-xl border-2 border-black bg-white">
              <h3 className="text-2xl font-serif font-bold text-black mb-4">
                Organisez votre événement
              </h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Anniversaires, événements d'entreprise, soirées privées... Contactez-nous
                pour créer une expérience unique et mémorable.
              </p>
              <div className="space-y-4">
                <a
                  href={`tel:${restaurantInfo.phone}`}
                  className="block bg-black text-white px-4 py-4 rounded-md font-semibold hover:bg-gray-800 transform hover:-translate-y-0.5 transition-all duration-200 shadow-md hover:shadow-lg w-full text-center"
                >
                  Appelez-nous
                </a>
                <a
                  href={`mailto:${restaurantInfo.email}`}
                  className="block bg-white text-black border-2 border-black px-4 py-4 rounded-md font-semibold hover:bg-gray-50 transform hover:-translate-y-0.5 transition-all duration-200 w-full"
                >
                  <div className="text-center break-words">
                    {restaurantInfo.email}
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VIPSection;
